<template>
  <AvsTabs
    v-model="tab"
    :loading="loading"
  >
    <template v-slot:tabs>
      <v-tab>
        <v-icon left>mdi-magnify-scan</v-icon>
        {{ $t("commons.informations") }}
      </v-tab>
      <v-tab :disabled="!isTemplateCreated()">
        <v-icon left>mdi-clipboard-text-outline</v-icon>
        {{ $t("commons.edition") }}
      </v-tab>
      <v-tab :disabled="!isTemplateCreated()">
        <v-icon left>mdi-eye-outline</v-icon>
        {{ $t("commons.preview") }}
      </v-tab>
    </template>

    <template v-slot:items>
      <v-tab-item key="0">
        <TemplateForm
          v-model="template"
          :isEditable="isEditable"
          :rulesUpdate="pRulesUpdate"
          @success="v => handleSuccess(v, 1)"
          @error="handleError"
        />
      </v-tab-item>
      <v-tab-item key="1">
        <TemplateDetailsForm
          v-model="templateDetails"
          :isEditable="isEditable"
          @success="v => handleSuccess(v, 2)"
          @error="handleError"
        />
      </v-tab-item>
      <v-tab-item key="2">
        <TemplatePreview :template="templateDetails" />
      </v-tab-item>
    </template>
  </AvsTabs>
</template>

<script>
import { TemplateService } from "@/services/documents"
import { STATE_ENUM } from "@/utils/consts"
import { mkTemplate } from "@/utils/datamodels"
import { required, slug, mkUnique } from "@/utils/validator"

export default {
  name: "TemplateEditor",
  components: {
    AvsTabs: () => import("@/components/generics/general/AvsTabs.vue"),
    TemplatePreview: () => import("@/components/ri3/documents/TemplatePreview.vue"),
    TemplateForm: () => import("@/components/ri3/documents/TemplateForm.vue"),
    TemplateDetailsForm: () => import("@/components/ri3/documents/TemplateDetailsForm.vue")
  },
  props: {
    templateId: {
      type: Number,
      required: true
    },
    rulesUpdate: {
      type: Object,
      default: () => ({})
    }
  },
  created() {
    this.loadTemplateForm()
  },
  data() {
    return {
      isEditable: false,
      isTemplateDetailsUpdated: false,
      loading: true,
      pRulesUpdate: undefined,
      tab: 0,
      template: mkTemplate(),
      templateDetails: undefined
    }
  },
  methods: {
    isTemplateCreated() {
      return this.template?.id > 0
    },
    async fetchTemplate() {
      let template_editable = 1
      if (this.templateId > 0) {
        this.template = await TemplateService.get(this.templateId)
        template_editable = this.template.state.id
      } else {
        let companyId = this.$store.getters["app/getCompany"].pk
        let lastTemplate = await TemplateService.get_latest(companyId)
        if (lastTemplate.id) {
          lastTemplate.id = null
          this.template = lastTemplate
        }
      }

      // Update isEditable flag
      this.isEditable = template_editable === STATE_ENUM.EDITABLE.id
    },
    async fetchTemplateDetails() {
      this.templateDetails = await TemplateService.details.get(this.template.id)
      this.isTemplateDetailsUpdated = true
    },
    async fetchRulesName() {
      if (this.rulesUpdate.name) {
        this.pRulesUpdate = this.rulesUpdate.name
      } else {
        const templates = await TemplateService.getAll(this.$store.getters["app/getCompany"].pk)
        const namelist = templates.filter(e => e.name !== this.template.name).map(e => e.name)
        this.pRulesUpdate = {
          ...this.rulesUpdate,
          name: [required, slug, mkUnique(namelist, "name")]
        }
      }
    },
    async loadTemplateForm() {
      this.loading = true
      this.fetchTemplate()
        .then(() => {
          if (this.isEditable) this.fetchRulesName()
        })
        .finally(() => {
          this.loading = false
        })
    },
    async loadTemplateDetailsForm() {
      if (this.isTemplateCreated() && !this.isTemplateDetailsUpdated) {
        this.loading = true
        this.fetchTemplateDetails().finally(() => {
          this.loading = false
        })
      }
    },
    handleSuccess(isSaved, nextTab) {
      // Handle message display
      const data = isSaved
        ? { msg: this.$t("messages.success.operation"), color: "success" }
        : { msg: this.$t("messages.success.no_updated"), color: "info" }
      this.$store.commit("app/message", data)

      // Handle next action
      this.tab = nextTab
    },
    handleError(msg) {
      // Handle message display
      this.$store.commit("app/message", { msg, color: "error" })
    }
  },
  watch: {
    template: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.isTemplateDetailsUpdated = false
        }
      }
    },
    tab: {
      handler(newValue, oldValue) {
        if (newValue === 1 || newValue === 2) this.loadTemplateDetailsForm()
      }
    }
  }
}
</script>
